import { useEffect, useState, useRef } from "react";

interface ParallaxOptions {
  speed?: number; // 0.1 to 1, lower = slower movement
  direction?: "up" | "down";
  disabled?: boolean; // Disable on mobile for performance
}

/**
 * Hook for parallax scrolling effects
 * @param options - Parallax configuration
 * @returns Transform style object to apply to element
 */
export const useParallax = (options: ParallaxOptions = {}) => {
  const { speed = 0.5, direction = "up", disabled = false } = options;
  const [offset, setOffset] = useState(0);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return;

    let rafId: number | null = null;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        
        rafId = requestAnimationFrame(() => {
          if (!elementRef.current) {
            ticking = false;
            return;
          }

          const rect = elementRef.current.getBoundingClientRect();
          const scrolled = window.scrollY;
          const elementTop = rect.top + scrolled;
          const windowHeight = window.innerHeight;

          // Only calculate parallax when element is in or near viewport
          if (rect.top < windowHeight && rect.bottom > 0) {
            const distance = scrolled - elementTop + windowHeight;
            const movement = distance * speed;
            setOffset(direction === "up" ? -movement : movement);
          }
          
          ticking = false;
        });
      }
    };

    // Use passive listener for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [speed, direction, disabled]);

  return {
    ref: elementRef,
    style: disabled ? {} : {
      transform: `translate3d(0, ${offset}px, 0)`,
      willChange: "transform",
    }
  };
};

/**
 * Hook for scale parallax effect (zoom in/out on scroll)
 * @param options - Parallax configuration
 * @returns Transform style object
 */
export const useScaleParallax = (options: ParallaxOptions = {}) => {
  const { speed = 0.0002, disabled = false } = options;
  const [scale, setScale] = useState(1);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return;

    let rafId: number | null = null;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        
        rafId = requestAnimationFrame(() => {
          if (!elementRef.current) {
            ticking = false;
            return;
          }

          const rect = elementRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // Only calculate when element is in viewport
          if (rect.top < windowHeight && rect.bottom > 0) {
            const scrollProgress = (windowHeight - rect.top) / windowHeight;
            const scaleValue = 1 + (scrollProgress * speed * 100);
            setScale(Math.max(1, Math.min(1.15, scaleValue))); // Clamp between 1 and 1.15
          }
          
          ticking = false;
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [speed, disabled]);

  return {
    ref: elementRef,
    style: disabled ? {} : {
      transform: `scale(${scale})`,
      willChange: "transform",
    }
  };
};
